import { useCallback } from 'react';
import type { DocumentAttachment } from '@/hooks/useTransactionDocumentDownload';
import { getAdmAuthHeaders } from '@/hooks/useTransactionDocuments';
import { useAuth } from '@/providers/AuthProvider';
import { useDocumentApi } from '@/providers/DocumentApiProvider';
import { useMode } from '@/providers/ModeProvider';

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const quoted = /(?:^|;)\s*filename\s*=\s*"([^"]+)"/.exec(header);
  if (quoted) return quoted[1];
  const unquoted = /(?:^|;)\s*filename\s*=\s*([^;\s"]+)/.exec(header);
  return unquoted?.[1] ?? null;
}

interface UseAdmDocumentDownloadAsBase64 {
  downloadAdmDocumentAsBase64: (
    documentId: string,
    fileNameHint?: string,
    contentTypeHint?: string,
  ) => Promise<DocumentAttachment>;
}

export function useAdmDocumentDownloadAsBase64(): UseAdmDocumentDownloadAsBase64 {
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();
  const { isDemo } = useMode();

  const downloadAdmDocumentAsBase64 = useCallback(
    async (documentId: string, fileNameHint?: string, contentTypeHint?: string): Promise<DocumentAttachment> => {
      if (isDemo) {
        return {
          base64Content: '',
          fileName: 'demo_document.pdf',
          contentType: 'application/pdf',
        };
      }

      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error('ADM not initialized — could not discover instance');
      }

      const headers = await getAdmAuthHeaders(getAccessToken);
      delete headers['Content-Type'];

      const response = await fetch(`${ctx.instanceUrl}/Documents/${documentId}/Download`, { method: 'GET', headers });

      if (!response.ok) {
        throw new Error(`Failed to download ADM document ${documentId} (HTTP ${response.status})`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileName =
        fileNameHint ?? parseFilenameFromContentDisposition(contentDisposition) ?? `document_${documentId}`;
      const contentType = contentTypeHint ?? response.headers.get('Content-Type') ?? 'application/octet-stream';

      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      return { base64Content, fileName, contentType };
    },
    [getDocumentContext, getAccessToken, isDemo],
  );

  return { downloadAdmDocumentAsBase64 };
}
